<template>
  <div class="user-status-filter-item activity">
    <label
      class="item-label"
      v-t.preserve="'appUserStatusFilterActivity.activity'"
    />
    <div class="action-container">
      <filter-button-group
        bordered
        v-model="localValue.isStill"
        v-bind:buttons="buttons"
      />
      <div v-if="localValue.isStill" class="criteria-container">
        <label class="label">
          <b-form-checkbox size="md" v-model="localValue.canSetDays" />
          <span
            class="text"
            v-t.preserve="
              'appUserStatusFilterActivity.message.specifyStillDays'
            "
          />
        </label>
        <div class="forms">
          <b-form-input
            lazy
            number
            size="sm"
            type="number"
            min="0"
            step="1"
            v-bind:disabled="!localValue.canSetDays"
            v-model="inputValue"
            v-on:blur="$_applyToStillDays"
            v-on:keydown.enter="$_applyToStillDays"
            class="days-form"
          />
          <span class="text unit">
            {{ $tc('appUserStatusFilterActivity.days', +localValue.stillDays) }}
          </span>
          <b-form-radio-group
            v-bind:disabled="!localValue.canSetDays"
            v-model="localValue.stillDaysOperator"
            class="operator-selector"
          >
            <label class="label">
              <b-form-radio v-bind:value="STILL_DAYS_OPERATOR.LESS" />
              <span
                class="text"
                v-t.preserve="'appUserStatusFilterActivity.operator.less'"
              />
            </label>
            <label class="label">
              <b-form-radio v-bind:value="STILL_DAYS_OPERATOR.GREATER" />
              <span
                class="text"
                v-t.preserve="'appUserStatusFilterActivity.operator.greater'"
              />
            </label>
          </b-form-radio-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FilterButtonGroup from '@/components/AppFilterButtonGroup.vue'
import { STILL_DAYS_OPERATOR } from '@/composables/member/UseUserStatusFilterCondition'

export default {
  name: 'AppUserStatusFilterActivity',
  components: {
    FilterButtonGroup,
  },
  props: {
    /**
     * 選択値
     * 「すべて」を選択する場合は null にする
     */
    isStill: {
      type: Boolean,
      default: null,
    },
    /**
     * 停止日数を指定可能な場合はtrue
     */
    canSetDays: {
      type: Boolean,
      default: false,
    },
    /**
     * 停止期間日数
     */
    stillDays: {
      type: Number,
      default: null,
    },
    /**
     * 停止期間の比較演算子
     * @values 'ge', 'le'
     */
    stillDaysOperator: {
      type: String,
      default: null,
    },
  },
  setup() {
    return { STILL_DAYS_OPERATOR }
  },
  data: function () {
    return {
      // ローカル値
      localValue: {
        isStill: null,
        canSetDays: false,
        stillDays: null,
        stillDaysOperator: null,
      },
      // 日数フォームの v-model 用値
      inputValue: null,
    }
  },
  computed: {
    /**
     * 選択肢のボタン定義
     * @type {Array.<Object>}
     */
    buttons: function () {
      return [
        { value: null, text: this.$t('appUserStatusFilterActivity.all') },
        { value: false, text: this.$t('appUserStatusFilterActivity.working') },
        { value: true, text: this.$t('appUserStatusFilterActivity.still') },
      ]
    },
    /**
     * 日数指定が有効な場合はtrue
     * @type {Boolean}
     */
    isDaysValid: function () {
      const { stillDays: days, stillDaysOperator: operator } = this.localValue

      // 0以上の整数値でない場合は NG
      if (days === '' || days == null || +days < 0) return false
      // 演算子未選択は NG
      if (!operator) return false
      // 全てクリアしたら OK
      return true
    },
    /**
     * イベント返却用の日数表記テキスト
     * @type {String}
     */
    criteriaText: function () {
      const {
        canSetDays,
        stillDays: days,
        stillDaysOperator: operator,
      } = this.localValue

      if (!canSetDays || !this.isDaysValid) {
        return ''
      } else if (operator === STILL_DAYS_OPERATOR.GREATER) {
        return this.$tc(
          'appUserStatusFilterActivity.message.daysCriteria.greater',
          days
        )
      } else {
        return this.$tc(
          'appUserStatusFilterActivity.message.daysCriteria.less',
          days
        )
      }
    },
    /**
     * イベント返却用のテキスト
     * @type {String}
     */
    resultText: function () {
      if (this.localValue.isStill == null) return ''

      const activity = this.buttons.find(
        (b) => b.value === this.localValue.isStill
      ).text

      if (this.localValue.isStill && this.criteriaText) {
        return `${activity}(${this.criteriaText})`
      } else {
        return activity
      }
    },
  },
  watch: {
    isStill: {
      immediate: true,
      handler: function (value) {
        this.localValue.isStill = value
      },
    },
    canSetDays: {
      immediate: true,
      handler: function (value) {
        this.localValue.canSetDays = value
      },
    },
    stillDays: {
      immediate: true,
      handler: function (value) {
        this.localValue.stillDays = value
      },
    },
    stillDaysOperator: {
      immediate: true,
      handler: function (value) {
        this.localValue.stillDaysOperator = value
      },
    },
    'localValue.stillDays': {
      immediate: true,
      handler: function (value) {
        this.inputValue = value
      },
    },
    localValue: {
      deep: true,
      handler: function () {
        this.$_onChange()
      },
    },
  },
  mounted: function () {
    // 初回結果表示のためにpropsの値で変更されたことにする
    this.$_onChange()
  },
  methods: {
    /**
     * 停止日数のフォーム入力値をローカル値に反映
     */
    $_applyToStillDays: function () {
      // 空文字を null に変換
      this.localValue.stillDays =
        this.inputValue === '' ? null : this.inputValue
    },

    /**
     * 変更時処理
     */
    $_onChange: function () {
      /**
       * 選択値変更イベント
       * @param {Object} value 選択値
       * @param {String} text 選択値表示テキスト
       */
      this.$emit('changed', { value: this.localValue, text: this.resultText })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../assets/scss/parts/user/user-status-filter-item';

::v-deep .filter-button-group {
  justify-content: flex-start;
}

.criteria-container {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;
  flex-wrap: wrap;

  .label {
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    margin: 0;
  }

  .forms {
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: nowrap;
  }

  .text {
    font-size: $font-size-small;
    user-select: none;
    white-space: nowrap;
    text-align: left;

    &.unit {
      margin-left: 4px;
    }
  }

  .days-form {
    width: 3rem;

    // スピンボタンを消す
    appearance: textfield;
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .operator-selector {
    width: auto;
    margin-left: 8px;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    column-gap: 8px;

    .label {
      display: inline-flex;
      justify-content: flex-start;
      align-items: center;
      margin: 0;
    }

    ::v-deep .custom-control {
      // テキストやフォームをラジオボタンに少し近づけるため右側 margin を調整
      margin: 0 -2px 0 0;
    }

    ::v-deep .custom-control-input {
      // disabled 時のスタイル => 全く選択されていないように見せる
      &:disabled ~ .custom-control-label {
        &::before {
          border-color: #adb5bd;
          background-color: #e9ecef;
        }
        &::after {
          display: none;
        }
      }

      // 選択時のスタイル => 色を緑系にする
      &:not(:disabled):checked ~ .custom-control-label::before {
        border-color: $entity-color-green;
        background-color: $entity-color-green;
      }
      // focus時のshadowは出さない
      &:focus ~ .custom-control-label::before {
        box-shadow: none;
      }
    }
  }
}
</style>
